import React, { useEffect, useMemo, useState } from 'react';

import { getDate, setDate } from 'date-fns';
import { debounce } from 'lodash';
import { isNil, not } from 'ramda';
import { useTranslation } from 'react-i18next';

import { RuleName } from '../types';

import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';

import { TimePicker } from '@mui/x-date-pickers-pro';
import { Box, TextField } from '@mui/material';

interface Props {
  value: Date;
  onChange: (date: Date | null) => void;
  ruleVariableName: RuleName;
  error?: boolean;
  helperText?: string;
}

export const TimestampRule: React.FC<Props> = ({
  ruleVariableName,
  value,
  onChange,
  error,
  helperText
}) => {
  const { t } = useTranslation();

  const [selectedDay, setSelectedDay] = useState<number>(getDate(value));
  const [selectedTime, setSelectedTime] = useState<Date | null>(
    setDate(value, 1)
  );

  const timestamp = useMemo(() => {
    if (!isNil(selectedTime)) {
      const newDate = new Date(selectedTime);
      newDate.setDate(selectedDay);
      return newDate.getTime();
    }
    return null;
  }, [selectedTime, selectedDay]);

  const handleDaySelect = (value) => {
    if (not(isNaN(value))) {
      setSelectedDay((prevState) => (value === prevState ? prevState : value));
    }
  };

  const handleTimeSelect = debounce((selectedTime) => {
    setSelectedTime((prevState) => {
      return isNil(selectedTime)
        ? new Date().toLocaleTimeString(navigator.language, {
            hour: '2-digit',
            minute: '2-digit'
          })
        : isNil(prevState)
        ? selectedTime
        : selectedTime.getTime() === prevState.getTime()
        ? prevState
        : selectedTime;
    });
  }, 500);

  useEffect(() => {
    if (isNil(timestamp)) {
      onChange(null);
    } else {
      onChange(new Date(timestamp));
    }
  }, [timestamp]);

  useEffect(() => {
    if (value) {
      handleDaySelect(value.getDate() ?? 1);
      handleTimeSelect(value);
    }
  }, [value]);

  return (
    <Box display="flex" gap={2.5}>
      <Select
        sx={{
          ...(error ? { marginBottom: '21.5px' } : {})
        }}
        error={error}
        value={selectedDay}
        onChange={(e) => handleDaySelect(e.target.value)}
      >
        <MenuItem value={1}>{t('day').concat(' 1')}</MenuItem>
        <MenuItem value={2}>{t('day').concat(' 2')}</MenuItem>
      </Select>
      <TimePicker
        ampm={false}
        value={selectedTime}
        onChange={handleTimeSelect}
        label={t(`dtrRules.variables.${ruleVariableName}`)}
        renderInput={(params) => (
          <TextField {...params} error={error} helperText={helperText} />
        )}
      />
    </Box>
  );
};
